import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { useI18n } from "i18n";
import { useContext, useEffect, useMemo, useState } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { IEvent } from "modules/event/types";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import DOMPurify from "dompurify";
import { useCustomRegistrationQuestions } from "../../hooks/useCustomRegistrationQuestions";
import { useStyles } from "./styles";
import { CustomQuestion } from "../CustomQuestion";
import { useAnswerOperations } from "../../hooks/useAnswerOperations";

interface Props {
  eventId: string;
  eventData: IEvent;
}

export const CustomQuestionsForm = ({ eventId, eventData }: Props) => {
  const {
    checkIfEventHappeningNow,
    checkIfEventIsUpcoming,
    checkIsAllowedToJoinEventLobby,
  } = useCheckEventStatus();
  const getEventStatus = (event: IEvent) => {
    if (checkIfEventHappeningNow(event)) {
      return "ongoing";
    }

    if (checkIsAllowedToJoinEventLobby(event)) {
      return "lobby";
    }

    if (checkIfEventIsUpcoming(event)) {
      return "upcoming";
    }

    return "past";
  };

  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["eventLanding", "common"]);
  const { questions, status: questionStatus } =
    useCustomRegistrationQuestions(eventId);
  const { submitAnswers, isPending } = useAnswerOperations(eventId, {
    onSuccess: () => {
      track(Events.CUSTOM_QUESTION_ANSWERED, {
        eventId,
        eventType: eventData.eventType,
        eventOutcome: eventData.eventOutcome,
        eventStatus: getEventStatus(eventData),
        isPrivate: eventData.isPrivate,
      });
    },
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const styles = useStyles();

  const handleAnswerChange = (id: string, answer: string) => {
    setAnswers({
      ...answers,
      [id]: answer,
    });
  };

  const handleSubmit = () => {
    submitAnswers(answers);
  };

  const isSubmitButtonDisabled = useMemo(
    () =>
      questions.some(
        (question) =>
          question.isRequired &&
          (!answers[question._id] || answers[question._id].trim() === ""),
      ),
    [answers, questions],
  );

  useEffect(() => {
    if (questionStatus === "success" && questions.length > 0) {
      // use reduce to create the initial record, with keys as question ids
      // and values as empty strings
      setAnswers(
        questions.reduce(
          (acc, question) => ({ ...acc, [question._id]: "" }),
          {},
        ),
      );
    }
  }, [questionStatus, questions]);

  useEffect(() => {
    track(Events.CUSTOM_QUESTION_SHOWN, {
      eventId,
      eventType: eventData.eventType,
      eventOutcome: eventData.eventOutcome,
      eventStatus: getEventStatus(eventData),
      isPrivate: eventData.isPrivate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      fullWidth
      classes={{
        root: styles.dialog,
        paperScrollPaper: styles.paperScrollPaper,
      }}
      maxWidth="sm"
      open
      disableEscapeKeyDown
    >
      <DialogContent classes={{ root: styles.dialogContent }}>
        <Typography variant="h4">
          {t("custom.registration.form.header")}
        </Typography>
        <Typography variant="h5" className={styles.subtitle}>
          {t("custom.registration.form.subtitle")}
        </Typography>
        <Box className={styles.questions}>
          {questions.map((question) => (
            <CustomQuestion
              question={question}
              onAnswerChange={handleAnswerChange}
            />
          ))}
          {eventData?.isNetworkingRecommendationsEnabled && (
            <Typography
              variant="body1"
              className={styles.disclaimer}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  t("custom.registration.form.disclaimer"),
                  {
                    ALLOWED_ATTR: ["target", "href", "class", "rel"],
                  },
                ),
              }}
            />
          )}
          <Button
            variant="secondary"
            color="blue"
            disabled={isSubmitButtonDisabled || isPending}
            loading={isPending}
            onClick={handleSubmit}
            data-testid="save-button"
            className={styles.continue}
          >
            {t("common:continue")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
